/**
 * Centralized style constants and utilities
 */

/**
 * Common gradient patterns
 */
export const Gradients = {
  // Brand gradient (3-color)
  brandPrimary: 'bg-gradient-to-r from-orange-600 via-purple-600 to-blue-600',
  brandVertical: 'bg-gradient-to-b from-orange-600 via-purple-600 to-blue-600',
  brandDiagonal: 'bg-gradient-to-br from-orange-600 via-purple-600 to-blue-600',

  // Simple 2-color gradients
  orangeBlue: 'bg-gradient-to-br from-orange-600 to-blue-600',
  blueOrange: 'bg-gradient-to-br from-blue-600 to-orange-600',

  // Hero backgrounds
  heroOrangeBlue: 'bg-gradient-to-br from-orange-50 to-blue-50',
  heroBlueOrange: 'bg-gradient-to-br from-blue-50 to-orange-50',
  heroDark: 'bg-gradient-to-br from-gray-900 to-gray-800',
  heroBlue: 'bg-gradient-to-br from-blue-900 to-blue-700',

  // Card backgrounds (light)
  cardLightOrange: 'bg-gradient-to-br from-orange-100 to-blue-100',
  cardLightBlue: 'bg-gradient-to-br from-blue-100 to-orange-100',

  // Card backgrounds (medium)
  cardOrange: 'bg-gradient-to-br from-orange-200 to-blue-200',
  cardBlue: 'bg-gradient-to-br from-blue-200 to-orange-200',

  // Monochrome gradients
  orangeSubtle: 'bg-gradient-to-br from-orange-50 to-orange-100',

  // Overlays
  overlayDark: 'bg-gradient-to-r from-gray-900/90 to-gray-900/70',

  // Text gradients
  textBrand: 'bg-gradient-to-r from-orange-600 via-purple-600 to-blue-600 bg-clip-text text-transparent',
} as const

/**
 * Common color variants
 */
export const Colors = {
  primary: 'orange',
  secondary: 'blue',
  success: 'green',
  accent: 'purple',
} as const

/**
 * Brand colors with intensity variants
 */
export const BrandColors = {
  orange: {
    bg: 'bg-orange-600',
    bgHover: 'hover:bg-orange-700',
    text: 'text-orange-600',
    textHover: 'hover:text-orange-600',
    bgLight: 'bg-orange-50',
    bgMedium: 'bg-orange-100',
    border: 'border-orange-600',
  },
  blue: {
    bg: 'bg-blue-600',
    bgHover: 'hover:bg-blue-700',
    text: 'text-blue-600',
    textHover: 'hover:text-blue-600',
    bgLight: 'bg-blue-50',
    bgMedium: 'bg-blue-100',
    border: 'border-blue-600',
  },
  green: {
    bg: 'bg-green-600',
    bgHover: 'hover:bg-green-700',
    text: 'text-green-600',
    textHover: 'hover:text-green-600',
    bgLight: 'bg-green-50',
    bgMedium: 'bg-green-100',
    border: 'border-green-600',
  },
  purple: {
    bg: 'bg-purple-600',
    bgHover: 'hover:bg-purple-700',
    text: 'text-purple-600',
    textHover: 'hover:text-purple-600',
    bgLight: 'bg-purple-50',
    bgMedium: 'bg-purple-100',
    border: 'border-purple-600',
  },
} as const

/**
 * Animation utilities
 */
export const Animations = {
  /**
   * Generate staggered animation delay
   * @param index - The index of the element
   * @param delayMs - Base delay in milliseconds (default: 100ms)
   * @returns CSS animation-delay value
   */
  staggerDelay: (index: number, delayMs: number = 100): string => {
    return `${(delayMs * (index + 1)) / 1000}s`
  },

  /**
   * Common animation classes
   */
  slideUp: 'animate-slide-up',
  fadeIn: 'animate-fade-in',

  /**
   * Transition classes
   */
  transition: 'transition-all duration-300',
  transitionSlow: 'transition-all duration-500',
  transitionFast: 'transition-all duration-150',
} as const

/**
 * Common shadow utilities
 */
export const Shadows = {
  card: 'shadow-sm hover:shadow-xl',
  cardLarge: 'shadow-lg hover:shadow-2xl',
  button: 'shadow-lg hover:shadow-xl',
} as const

/**
 * Typography scale
 */
export const Typography = {
  // Display sizes (for heroes)
  display: {
    xl: 'text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight',
    lg: 'text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight',
    md: 'text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight',
  },
  // Heading sizes
  heading: {
    h1: 'text-4xl sm:text-5xl lg:text-6xl font-bold',
    h2: 'text-3xl sm:text-4xl lg:text-5xl font-bold',
    h3: 'text-2xl sm:text-3xl lg:text-4xl font-semibold',
    h4: 'text-xl sm:text-2xl lg:text-3xl font-semibold',
  },
  // Body sizes
  body: {
    xl: 'text-xl sm:text-2xl leading-relaxed',
    lg: 'text-lg sm:text-xl leading-relaxed',
    md: 'text-base sm:text-lg leading-relaxed',
    sm: 'text-sm leading-relaxed',
  },
  // Label/caption
  label: {
    lg: 'text-sm font-semibold uppercase tracking-wider',
    md: 'text-xs font-semibold uppercase tracking-wider',
    sm: 'text-[10px] font-bold uppercase tracking-wider',
  },
} as const

/**
 * Spacing scale
 */
export const Spacing = {
  section: {
    sm: 'py-8 sm:py-12',
    md: 'py-12 sm:py-16',
    lg: 'py-16 sm:py-24',
    xl: 'py-24 sm:py-32',
  },
  stack: {
    sm: 'space-y-2',
    md: 'space-y-4',
    lg: 'space-y-6',
    xl: 'space-y-8',
  },
  inline: {
    sm: 'space-x-2',
    md: 'space-x-4',
    lg: 'space-x-6',
  },
  margin: {
    section: 'mb-16',
    block: 'mb-8',
    element: 'mb-4',
  },
} as const

/**
 * Border radius standards
 */
export const BorderRadius = {
  sm: 'rounded-lg',
  md: 'rounded-xl',
  lg: 'rounded-2xl',
  xl: 'rounded-3xl',
  full: 'rounded-full',
} as const

/**
 * Hero height variants
 */
export const HeroHeight = {
  full: 'min-h-screen',
  large: 'min-h-[80vh]',
  medium: 'min-h-[60vh]',
} as const

/**
 * Helper function to combine class names
 */
export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

/**
 * Apply gradient text effect to an element (Hero title style)
 * Gradient: White → Peach → Orange
 */
export function applyGradientText(element: HTMLElement, variant: 'hero' | 'dark' = 'hero'): void {
  if (variant === 'hero') {
    // White to orange gradient for dark backgrounds
    element.style.background = 'linear-gradient(135deg, #FFFFFF 0%, #F8B7A5 50%, #EF5730 100%)'
  } else {
    // Dark gradient for light backgrounds
    element.style.background = 'linear-gradient(135deg, #0F172A 0%, #334155 50%, #EF5730 100%)'
  }
  element.style.webkitBackgroundClip = 'text'
  element.style.webkitTextFillColor = 'transparent'
  element.style.backgroundClip = 'text'
}

/**
 * Apply drop shadow to gradient text for better visibility
 */
export function applyTextShadow(element: HTMLElement): void {
  element.style.filter = 'drop-shadow(0 4px 20px rgba(0,0,0,0.5))'
}
